import { useEffect, useRef, useState } from "react";
import { api, extractError } from "@/lib/api";
import { formatDateID, formatTimeID, formatIDR } from "@/lib/format";
import { Send, Plus, X, Search, Trash2, Megaphone } from "lucide-react";
import { toast } from "sonner";

function ChatWorkspace({ labels, templates, reloadMeta }) {
  const [threads, setThreads] = useState([]);
  const [labelFilter, setLabelFilter] = useState("");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [summary, setSummary] = useState(null);
  const [quickLabel, setQuickLabel] = useState({ name: "", color: "#7C3AED" });
  const bottomRef = useRef();

  const loadThreads = () => api.get("/admin/chat/threads", { params: { label: labelFilter, q } }).then((r) => setThreads(r.data)).catch(() => {});
  useEffect(() => { loadThreads(); const t = setInterval(loadThreads, 8000); return () => clearInterval(t); }, [labelFilter, q]);

  const openThread = (id) => api.get(`/admin/chat/threads/${id}/messages`).then((r) => {
    setActive(r.data.thread);
    setMsgs(r.data.messages);
    loadThreads();
  }).catch(() => {});
  useEffect(() => {
    if (!active?.id) return;
    const t = setInterval(() => api.get(`/admin/chat/threads/${active.id}/messages`).then((r) => setMsgs(r.data.messages)).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [active?.id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const send = async () => {
    if (!text.trim() || !active) return;
    try {
      await api.post(`/admin/chat/threads/${active.id}/messages`, { text: text.trim() });
      setText("");
      openThread(active.id);
    } catch (e) { toast.error(extractError(e)); }
  };

  const toggleLabel = async (lid, on) => {
    try {
      await api.post(`/admin/chat/threads/${active.id}/labels`, { label_id: lid, on });
      openThread(active.id);
    } catch (e) { toast.error(extractError(e)); }
  };

  useEffect(() => {
    if (!active?.id) return setSummary(null);
    api.get(`/admin/chat/threads/${active.id}/customer-summary`).then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, [active?.id]);

  const quickAddLabel = async () => {
    if (!quickLabel.name.trim()) return toast.error("Isi nama label");
    try {
      await api.post("/admin/chat/labels", quickLabel);
      setQuickLabel({ name: "", color: "#7C3AED" });
      reloadMeta?.();
      toast.success("Label custom ditambahkan — langsung bisa dipakai di chat ini");
    } catch (e) { toast.error(extractError(e)); }
  };

  const labelName = (lid) => labels.find((l) => l.id === lid);

  return (
    <div className="grid lg:grid-cols-[280px_1fr_240px] gap-3 h-[calc(100vh-220px)] min-h-[420px]">
      {/* Thread list */}
      <div className="bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden">
        <div className="p-2.5 border-b border-slate-100 space-y-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="chat-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari pelanggan..." className="w-full h-8 pl-8 pr-2 rounded-lg border border-slate-200 text-xs" />
          </div>
          <select data-testid="chat-label-filter" value={labelFilter} onChange={(e) => setLabelFilter(e.target.value)} className="w-full h-8 px-2 rounded-lg border border-slate-200 text-xs bg-white">
            <option value="">Semua Label</option>
            {labels.length > 0 && (
              <optgroup label="Label Custom">
                {labels.map((l) => <option key={l.id} value={`custom:${l.id}`}>{l.name}</option>)}
              </optgroup>
            )}
          </select>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {threads.length === 0 && <div className="p-6 text-center text-xs text-slate-400">Belum ada percakapan.</div>}
          {threads.map((t) => (
            <button key={t.id} data-testid={`chat-thread-${t.id}`} onClick={() => openThread(t.id)}
              className={`w-full text-left p-3 hover:bg-slate-50 ${active?.id === t.id ? "bg-slate-50 border-l-2 border-kiyana-maroon" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold truncate">{t.customer_name}</span>
                {t.unread_admin > 0 && <span className="bg-kiyana-maroon text-white text-[9px] rounded-full min-w-4 h-4 px-1 flex items-center justify-center flex-shrink-0">{t.unread_admin}</span>}
              </div>
              <div className="text-[11px] text-slate-500 truncate mt-0.5">{t.last_message || "Belum ada pesan"}</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {(t.labels || []).map((lid) => {
                  const l = labelName(lid);
                  return l ? <span key={lid} className="text-[9px] font-semibold rounded-full px-1.5 py-0.5" style={{ background: l.color + "22", color: l.color }}>{l.name}</span> : null;
                })}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div className="bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-xs text-slate-400">Pilih percakapan untuk mulai membalas.</div>
        ) : (
          <>
            <div className="p-3 border-b border-slate-100">
              <div className="text-sm font-semibold">{active.customer_name}</div>
              <div className="text-[11px] text-slate-500">{active.customer_email}</div>
            </div>
            <div data-testid="admin-chat-messages" className="flex-1 overflow-y-auto p-3 space-y-2.5">
              {msgs.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-xl px-3 py-2 ${m.sender === "admin" ? "bg-kiyana-maroon text-white" : "bg-slate-100 text-slate-700"}`}>
                    {m.sender === "admin" && (
                      <div className="text-[9px] opacity-70 mb-0.5 flex items-center gap-1">
                        {m.broadcast && <Megaphone className="w-2.5 h-2.5" />}
                        {m.sender_name}{m.auto ? " • Otomatis" : ""}{m.broadcast ? " • Broadcast" : ""}
                      </div>
                    )}
                    <div className="text-xs leading-relaxed whitespace-pre-line">{m.text}</div>
                    <div className={`text-[9px] mt-0.5 ${m.sender === "admin" ? "text-white/70 text-right" : "text-slate-400"}`}>{formatTimeID(m.created_at)}</div>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <div className="p-2.5 border-t border-slate-100 space-y-2">
              {templates.length > 0 && (
                <select data-testid="chat-template-pick" defaultValue="" onChange={(e) => { const t = templates.find((x) => x.id === e.target.value); if (t) setText((prev) => (prev ? prev + "\n" : "") + t.text); e.target.value = ""; }}
                  className="w-full h-8 px-2 rounded-lg border border-slate-200 text-xs bg-white text-slate-500">
                  <option value="" disabled>Sisipkan template pesan...</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              )}
              <div className="flex gap-2">
                <input data-testid="admin-chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Tulis balasan..." className="flex-1 h-9 px-3 rounded-lg border border-slate-200 text-xs" />
                <button data-testid="admin-chat-send" onClick={send} disabled={!text.trim()} className="h-9 px-4 rounded-lg bg-kiyana-maroon text-white text-xs font-semibold disabled:opacity-40 flex items-center gap-1">
                  <Send className="w-3.5 h-3.5" /> Kirim
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Customer panel */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 overflow-y-auto">
        {!active ? (
          <div className="text-xs text-slate-400 text-center py-6">Info pelanggan & label muncul di sini.</div>
        ) : (
          <>
            <div className="text-xs font-semibold mb-2">Label Custom</div>
            {labels.length === 0 && <div className="text-[11px] text-slate-400">Belum ada label custom — buat di tab Pengaturan atau lewat tambah cepat di bawah.</div>}
            <div className="space-y-1.5">
              {labels.map((l) => {
                const on = (active.labels || []).includes(l.id);
                return (
                  <label key={l.id} data-testid={`assign-label-${l.id}`} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={on} onChange={(e) => toggleLabel(l.id, e.target.checked)} className="accent-pink-700" />
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: l.color }} />
                    {l.name}
                  </label>
                );
              })}
            </div>
            <div className="flex gap-1.5 mt-2">
              <input data-testid="quick-label-color" type="color" value={quickLabel.color} onChange={(e) => setQuickLabel({ ...quickLabel, color: e.target.value })} className="w-7 h-7 rounded-lg border border-slate-200 p-0.5 cursor-pointer flex-shrink-0" />
              <input data-testid="quick-label-name" value={quickLabel.name} onChange={(e) => setQuickLabel({ ...quickLabel, name: e.target.value })} placeholder="Label baru..." className="flex-1 h-7 px-2 rounded-lg border border-slate-200 text-[11px] min-w-0" />
              <button data-testid="quick-label-add" onClick={quickAddLabel} className="h-7 px-2 rounded-lg bg-violet-600 text-white flex items-center flex-shrink-0"><Plus className="w-3.5 h-3.5" /></button>
            </div>

            <div className="text-xs font-semibold mt-5 mb-2">Riwayat Pelanggan</div>
            {!summary ? (
              <div className="text-[11px] text-slate-400">Memuat riwayat...</div>
            ) : (
              <div data-testid="customer-summary">
                <div className="grid grid-cols-2 gap-1.5 mb-3">
                  <div className="bg-slate-50 rounded-lg p-2"><div className="text-[9px] uppercase tracking-wide text-slate-400">Total Pesanan</div><div className="text-sm font-bold" data-testid="sum-orders">{summary.orders_total}</div></div>
                  <div className="bg-slate-50 rounded-lg p-2"><div className="text-[9px] uppercase tracking-wide text-slate-400">Total Belanja</div><div className="text-sm font-bold" data-testid="sum-spent">{formatIDR(summary.total_spent)}</div></div>
                  <div className="bg-red-50 rounded-lg p-2"><div className="text-[9px] uppercase tracking-wide text-red-400">Pembatalan</div><div className="text-sm font-bold text-red-600" data-testid="sum-cancelled">{summary.orders_cancelled}</div></div>
                  <div className="bg-orange-50 rounded-lg p-2"><div className="text-[9px] uppercase tracking-wide text-orange-400">Retur/Refund</div><div className="text-sm font-bold text-orange-600" data-testid="sum-refund">{summary.orders_refund}</div></div>
                </div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Pesanan Terakhir</div>
                {summary.last_orders.length === 0 && <div className="text-[11px] text-slate-400">Belum pernah pesan.</div>}
                <div className="space-y-1.5">
                  {summary.last_orders.map((o) => (
                    <div key={o.id} data-testid={`sum-order-${o.code}`} className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-2.5 py-1.5">
                      <div className="min-w-0">
                        <div className="text-[11px] font-mono font-semibold truncate">{o.code}</div>
                        <div className="text-[10px] text-slate-400">{formatDateID(o.created_at)}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-[11px] font-bold">{formatIDR(o.total)}</div>
                        <div className={`text-[9px] font-semibold ${o.order_status === "dibatalkan" ? "text-red-500" : o.order_status === "selesai" ? "text-emerald-600" : "text-slate-500"}`}>{(o.order_status || "-").replace(/_/g, " ")}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function BroadcastPanel({ labels }) {
  const [form, setForm] = useState({ name: "", message: "", target: "all" });
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/chat/broadcasts").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!form.name.trim() || !form.message.trim()) return toast.error("Isi nama dan pesan broadcast");
    try {
      const r = await api.post("/admin/chat/broadcasts", form);
      toast.success(`Broadcast terkirim ke ${r.data.sent_count} pelanggan`);
      setForm({ name: "", message: "", target: "all" });
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="font-semibold text-sm flex items-center gap-2"><Megaphone className="w-4 h-4 text-kiyana-maroon" /> Buat Broadcast Promosi</div>
        <input data-testid="bc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama broadcast (mis. Promo Payday)" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        <textarea data-testid="bc-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} placeholder="Isi pesan promosi ke pelanggan..." className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
        <select data-testid="bc-target" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="all">Semua pelanggan yang pernah chat</option>
          {labels.map((l) => <option key={l.id} value={`label:${l.id}`}>Hanya label: {l.name}</option>)}
        </select>
        <button data-testid="bc-send" onClick={send} className="h-10 px-6 rounded-lg bg-kiyana-maroon text-white text-sm font-semibold">Kirim Broadcast</button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="font-semibold text-sm mb-3">Riwayat Broadcast</div>
        <div className="divide-y divide-slate-100">
          {items.length === 0 && <div className="py-6 text-center text-xs text-slate-400">Belum ada broadcast.</div>}
          {items.map((b) => (
            <div key={b.id} data-testid={`bc-item-${b.id}`} className="py-2.5 text-xs">
              <div className="flex justify-between gap-2">
                <span className="font-semibold">{b.name}</span>
                <span className="text-slate-500">{formatDateID(b.created_at)}</span>
              </div>
              <div className="text-slate-500 line-clamp-2 mt-0.5">{b.message}</div>
              <div className="mt-1"><span className="bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 text-[10px] font-semibold">Terkirim ke {b.sent_count} pelanggan</span></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PerformaPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/chat/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  const mm = Math.floor((s?.avg_response_sec || 0) / 60);
  const ss = String((s?.avg_response_sec || 0) % 60).padStart(2, "0");
  const cards = [
    { label: "Jumlah Chat", value: s?.total_chats ?? "…", testid: "stat-total" },
    { label: "Persentase Chat Dibalas", value: s ? `${s.replied_pct}%` : "…", testid: "stat-replied" },
    { label: "Waktu Respons Rata-rata", value: s ? `${String(mm).padStart(2, "0")}:${ss}` : "…", testid: "stat-response" },
    { label: "Chat Belum Dibaca", value: s?.unread_total ?? "…", testid: "stat-unread" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {cards.map((c) => (
        <div key={c.label} data-testid={c.testid} className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{c.label}</div>
          <div className="text-xl font-display font-bold mt-1">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function ChatSettings({ labels, templates, reloadMeta }) {
  const [cfg, setCfg] = useState(null);
  const [newLabel, setNewLabel] = useState({ name: "", color: "#BE5A6E" });
  const [newTpl, setNewTpl] = useState({ title: "", text: "" });
  useEffect(() => { api.get("/admin/settings").then((r) => setCfg({ chat_auto_reply_enabled: r.data.chat_auto_reply_enabled, chat_auto_reply_text: r.data.chat_auto_reply_text })).catch(() => setCfg(false)); }, []);

  const saveCfg = async () => {
    try {
      await api.put("/admin/settings", cfg);
      toast.success("Pengaturan pesan otomatis tersimpan");
    } catch { toast.error("Gagal menyimpan — pengaturan ini khusus Owner"); }
  };

  const addLabel = async () => {
    if (!newLabel.name.trim()) return toast.error("Isi nama label");
    try {
      await api.post("/admin/chat/labels", newLabel);
      setNewLabel({ name: "", color: "#BE5A6E" });
      reloadMeta();
    } catch (e) { toast.error(extractError(e)); }
  };

  const delLabel = async (id) => {
    await api.delete(`/admin/chat/labels/${id}`).catch(() => {});
    reloadMeta();
  };

  const addTpl = async () => {
    if (!newTpl.title.trim() || !newTpl.text.trim()) return toast.error("Isi judul dan isi template");
    try {
      await api.post("/admin/chat/templates", newTpl);
      setNewTpl({ title: "", text: "" });
      reloadMeta();
    } catch (e) { toast.error(extractError(e)); }
  };

  const delTpl = async (id) => {
    await api.delete(`/admin/chat/templates/${id}`).catch(() => {});
    reloadMeta();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="font-semibold text-sm">Pesan Otomatis (Auto-Reply)</div>
        <p className="text-[11px] text-slate-500">Terkirim otomatis saat pelanggan memulai chat pertama kali.</p>
        {cfg === false ? <div className="text-xs text-red-500">Khusus Owner.</div> : cfg && (
          <>
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="auto-reply-toggle" type="checkbox" checked={cfg.chat_auto_reply_enabled} onChange={(e) => setCfg({ ...cfg, chat_auto_reply_enabled: e.target.checked })} className="w-4 h-4 accent-pink-700" />
              Aktifkan pesan otomatis
            </label>
            <textarea data-testid="auto-reply-text" value={cfg.chat_auto_reply_text} onChange={(e) => setCfg({ ...cfg, chat_auto_reply_text: e.target.value })} rows={4} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <button data-testid="auto-reply-save" onClick={saveCfg} className="h-9 px-4 rounded-lg bg-kiyana-maroon text-white text-xs font-semibold">Simpan</button>
          </>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="font-semibold text-sm">Template Pesan (Balasan Cepat)</div>
        <div className="space-y-2">
          {templates.length === 0 && <div className="text-[11px] text-slate-400">Belum ada template.</div>}
          {templates.map((t) => (
            <div key={t.id} data-testid={`tpl-${t.id}`} className="flex items-start gap-2 bg-slate-50 rounded-lg p-2.5">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold">{t.title}</div>
                <div className="text-[11px] text-slate-500 line-clamp-2">{t.text}</div>
              </div>
              <button data-testid={`tpl-del-${t.id}`} onClick={() => delTpl(t.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <input data-testid="tpl-title" value={newTpl.title} onChange={(e) => setNewTpl({ ...newTpl, title: e.target.value })} placeholder="Judul (mis. Info Jam Operasional)" className="w-full h-9 px-3 rounded-lg border border-slate-200 text-xs" />
        <textarea data-testid="tpl-text" value={newTpl.text} onChange={(e) => setNewTpl({ ...newTpl, text: e.target.value })} rows={2} placeholder="Isi pesan..." className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs" />
        <button data-testid="tpl-add" onClick={addTpl} className="h-9 px-4 rounded-lg bg-kiyana-maroon text-white text-xs font-semibold flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Tambah Template</button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="font-semibold text-sm">Label Custom Chat</div>
        <p className="text-[11px] text-slate-500">Buat label sendiri untuk mengelompokkan chat pelanggan (mis. VIP, Reseller), lalu centang di panel percakapan.</p>
        <div className="space-y-1.5">
          {labels.length === 0 && <div className="text-[11px] text-slate-400">Belum ada label custom.</div>}
          {labels.map((l) => (
            <div key={l.id} data-testid={`label-${l.id}`} className="flex items-center gap-2 bg-slate-50 rounded-lg px-2.5 py-2">
              <span className="w-3 h-3 rounded-full" style={{ background: l.color }} />
              <span className="text-xs font-medium flex-1">{l.name}</span>
              <button data-testid={`label-del-${l.id}`} onClick={() => delLabel(l.id)} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input data-testid="label-color" type="color" value={newLabel.color} onChange={(e) => setNewLabel({ ...newLabel, color: e.target.value })} className="w-9 h-9 rounded-lg border border-slate-200 p-0.5 cursor-pointer" />
          <input data-testid="label-name" value={newLabel.name} onChange={(e) => setNewLabel({ ...newLabel, name: e.target.value })} placeholder="Nama label (mis. VIP, Reseller)" className="flex-1 h-9 px-3 rounded-lg border border-slate-200 text-xs" />
        </div>
        <button data-testid="label-add" onClick={addLabel} className="h-9 px-4 rounded-lg bg-kiyana-maroon text-white text-xs font-semibold flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Tambah Label</button>
      </div>
    </div>
  );
}

export default function AdminChat() {
  const [tab, setTab] = useState("chat");
  const [labels, setLabels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const reloadMeta = () => {
    api.get("/admin/chat/labels").then((r) => setLabels(r.data)).catch(() => {});
    api.get("/admin/chat/templates").then((r) => setTemplates(r.data)).catch(() => {});
  };
  useEffect(() => { reloadMeta(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl font-bold">Chat Pelanggan</h1>
          <p className="text-sm text-slate-500">Balas chat pelanggan langsung dari backend — tanpa WhatsApp.</p>
        </div>
        <div className="flex gap-1 bg-white border border-slate-200 rounded-full p-1">
          {[["chat", "Percakapan"], ["broadcast", "Broadcast"], ["performa", "Performa"], ["setting", "Pengaturan"]].map(([id, label]) => (
            <button key={id} data-testid={`chat-tab-${id}`} onClick={() => setTab(id)}
              className={`px-4 h-8 rounded-full text-xs font-medium transition ${tab === id ? "bg-kiyana-maroon text-white" : "text-slate-600 hover:bg-slate-50"}`}>{label}</button>
          ))}
        </div>
      </div>
      {tab === "chat" && <ChatWorkspace labels={labels} templates={templates} reloadMeta={reloadMeta} />}
      {tab === "broadcast" && <BroadcastPanel labels={labels} />}
      {tab === "performa" && <PerformaPanel />}
      {tab === "setting" && <ChatSettings labels={labels} templates={templates} reloadMeta={reloadMeta} />}
    </div>
  );
}
